import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { ProtectedRoute } from '@/components/routing/ProtectedRoute';
import { useAuth } from '@/contexts/AuthContext';
import { lazyModule } from '@/lib/lazyModule';
import { ExplorerRedirect } from '@/pages/page_app/drive/ExplorerRedirect';

const Home = lazyModule(() => import('@/pages/Home'));
const About = lazyModule(() => import('@/pages/About'));
const Login = lazyModule(() => import('@/pages/auth/Login'));
const TwoFactorVerify = lazyModule(() => import('@/pages/auth/TwoFactorVerify'));
const Signup = lazyModule(() => import('@/pages/auth/Signup'));
const ForgotPassword = lazyModule(() => import('@/pages/auth/ForgotPassword'));
const Privacy = lazyModule(() => import('@/pages/legal/Privacy'));
const ResetPassword = lazyModule(() => import('@/pages/auth/ResetPassword'));
const ChangePassword = lazyModule(() => import('@/pages/auth/ChangePassword'));
const Logout = lazyModule(() => import('@/pages/auth/Logout'));
const NotFound = lazyModule(() => import('@/components/layout/NotFound'));

// SafeZone
const SafezoneLayout = lazyModule(() => import('@/pages/safezone/Layout'));
const SafezonePasswords = lazyModule(() => import('@/pages/safezone/passwords/Index'));
const SafezonePaymentCards = lazyModule(() => import('@/pages/safezone/payment-cards/Index'));
const SafezoneIdentities = lazyModule(() => import('@/pages/safezone/identities/Index'));
const SafezoneSecurity = lazyModule(() => import('@/pages/safezone/security/Index'));

// My Tasks
const TodosIndex = lazyModule(() => import('@/pages/todos/Index'));

// Contacts
const ContactsIndex = lazyModule(() => import('@/pages/contacts/Index'));
const CalendarIndex = lazyModule(() => import('@/pages/calendar/Index'));

// Finance
const FinanceLayout = lazyModule(() => import('@/pages/finance/Layout'));
const FinanceDashboard = lazyModule(() => import('@/pages/finance/Dashboard'));
const FinanceExpenses = lazyModule(() => import('@/pages/finance/expenses/Index'));
const FinanceIncome = lazyModule(() => import('@/pages/finance/income/Index'));
const FinanceLoans = lazyModule(() => import('@/pages/finance/loans/Index'));
const FinanceBudgets = lazyModule(() => import('@/pages/finance/budgets/Index'));
const FinanceTags = lazyModule(() => import('@/pages/finance/tags/Index'));

// Notes
const NotesIndex = lazyModule(() => import('@/pages/notes/Index'));
const NotesShow = lazyModule(() => import('@/pages/notes/Show'));

// Profile
const ProfileShow = lazyModule(() => import('@/pages/profile/Show'));
const ProfileDevices = lazyModule(() => import('@/pages/profile/devices/Index'));
const ProfileApiKeys = lazyModule(() => import('@/pages/profile/api_keys/Index'));


// Conversations
const ConversationsIndex = lazyModule(() => import('@/pages/conversations/Index'));

// AI Assistant
const AiAssistantIndex = lazyModule(() => import('@/pages/ai/Index'));

// Doc
const DocIndex = lazyModule(() => import('@/pages/doc/Index'));

// Pages
const PageAppDrive = lazyModule(() => import('@/pages/page_app/drive/Drive'));

// Workspace — split screen
const WorkspaceIndex = lazyModule(() => import('@/pages/workspace/Index'));

// FlowApp
const OauthAuthorize = lazyModule(() => import('@/pages/oauth/Authorize'));
const FlowAppLayout = lazyModule(() => import('@/pages/flow_app/Layout'));
const FlowAppIndex = lazyModule(() => import('@/pages/flow_app/Index'));
const FlowAppBoard = lazyModule(() => import('@/pages/flow_app/Board'));
const FlowAppBacklog = lazyModule(() => import('@/pages/flow_app/Backlog'));
const FlowAppCardShow = lazyModule(() => import('@/pages/flow_app/CardShow'));
const FlowAppProjectFields = lazyModule(() => import('@/pages/flow_app/ProjectFields'));
const FlowAppProjectColumns = lazyModule(() => import('@/pages/flow_app/ProjectColumns'));
const TableAppIndex = lazyModule(() => import('@/pages/table_app/Index'));
const TableAppTable = lazyModule(() => import('@/pages/table_app/TableScreen'));
const TableFormBuilder = lazyModule(() => import('@/pages/table_app/forms/FormBuilder'));
const TablePublicForm = lazyModule(() => import('@/pages/table_app/forms/PublicForm'));

function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Auth routes — redirect to home if already logged in */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/login/verify" element={<TwoFactorVerify />} />
        <Route
          path="/signup"
          element={isAuthenticated ? <Navigate to="/" replace /> : <Signup />}
        />
        {/* PUBLIC and outside MainLayout, deliberately: a store reviewer has
            no account, and both stores require a reachable privacy URL before
            a listing is approved. */}
        <Route path="/privacy" element={<Privacy />} />
        {/* A table form, as the people who fill it see it: PUBLIC, outside the
            app shell. Who may submit is the form's own access mode. */}
        <Route path="/f/:slug" element={<TablePublicForm />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />
        <Route path="/logout" element={<Logout />} />

        {/* Main layout wraps all app routes */}
        <Route element={<MainLayout />}>
          <Route index element={<Home />} />

          {/* About the creator — public, indexed by search engines */}
          <Route path="about" element={<About />} />

          {/* SafeZone */}
          <Route path="safezone" element={<SafezoneLayout />}>
            <Route index element={<Navigate to="passwords" replace />} />
            <Route path="passwords" element={<SafezonePasswords />} />
            <Route path="payment-cards" element={<SafezonePaymentCards />} />
            <Route path="identities" element={<SafezoneIdentities />} />
            <Route path="security" element={<SafezoneSecurity />} />
          </Route>

          {/* My Tasks */}
          <Route path="todos" element={<TodosIndex />} />

          {/* Contacts */}
          <Route path="contacts" element={<ContactsIndex />} />

          {/* Calendar — events, birthdays, anniversaries (docs/CALENDAR_APP.md) */}
          <Route path="calendar" element={<CalendarIndex />} />

          {/* Finance */}
          <Route path="finance" element={<FinanceLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<FinanceDashboard />} />
            <Route path="expenses" element={<FinanceExpenses />} />
            <Route path="income" element={<FinanceIncome />} />
            <Route path="loans" element={<FinanceLoans />} />
            <Route path="budgets" element={<FinanceBudgets />} />
            {/* Recurring is now a segment of Expenses — keep old links working */}
            <Route path="recurring" element={<Navigate to="/finance/expenses?view=recurring" replace />} />
            {/* Reports is now a sub-tab of the dashboard — keep old links working */}
            <Route path="reports" element={<Navigate to="/finance/dashboard?view=reports" replace />} />
            <Route path="tags" element={<FinanceTags />} />
          </Route>

          {/* Workspace — several apps on one screen */}
          <Route path="workspace" element={<WorkspaceIndex />} />

          {/* Notes */}
          <Route path="notes" element={<NotesIndex />} />
          <Route path="notes/:id" element={<NotesShow />} />

          {/* Profile */}
          <Route
            path="profile/me"
            element={
              <ProtectedRoute>
                <ProfileShow />
              </ProtectedRoute>
            }
          />
          <Route
            path="profile/me/devices"
            element={
              <ProtectedRoute>
                <ProfileDevices />
              </ProtectedRoute>
            }
          />
          <Route
            path="oauth/authorize"
            element={<OauthAuthorize />}
          />
          <Route
            path="profile/me/api-keys"
            element={
              <ProtectedRoute>
                <ProfileApiKeys />
              </ProtectedRoute>
            }
          />
          <Route path="profile/:id" element={<ProfileShow />} />

          {/* Conversations */}
          <Route path="conversations" element={<ConversationsIndex />} />

          {/* AI Assistant */}
          <Route path="ai" element={<AiAssistantIndex />} />

          {/* Doc – Sign, Merge & Convert */}
          <Route path="doc">
            <Route index element={<Navigate to="sign" replace />} />
            <Route path=":tab" element={<DocIndex />} />
          </Route>

          {/* Pages — the drive (docs/design/PAGES_UX_2026.md). /explorer was its old address. */}
          <Route path="pages" element={<PageAppDrive />} />
          <Route path="pages/:spaceId" element={<PageAppDrive />} />
          <Route path="explorer" element={<ExplorerRedirect />} />
          <Route path="explorer/:spaceId" element={<ExplorerRedirect />} />

          {/* FlowApp */}
          <Route path="flow" element={<FlowAppLayout />}>
            <Route index element={<FlowAppIndex />} />
            <Route path="backlog" element={<FlowAppBacklog />} />
          </Route>
          <Route path="flow/:projectId" element={<FlowAppBoard />} />
          <Route path="flow/:projectId/fields" element={<FlowAppProjectFields />} />
          <Route path="flow/:projectId/columns" element={<FlowAppProjectColumns />} />
          <Route path="flow/:projectId/cards/:cardId" element={<FlowAppCardShow />} />

          {/* Tables */}
          <Route path="tables" element={<TableAppIndex />} />
          <Route path="tables/:baseId" element={<TableAppTable />} />
          <Route path="tables/:baseId/:tableId" element={<TableAppTable />} />
          <Route path="tables/:baseId/:tableId/forms/:formId" element={<TableFormBuilder />} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
